import { FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { Box, useTheme } from '@mui/material'
import DragAndDrop from '../DragAndDrop'
import EventsGroup from '../EventsGroup'
import PlaybookReportsPanel from '../PlaybookReportsPanel'
import { PlaybookContext } from '../../../../contexts/playbooks.context'
import { IEvent } from '../../../../types/event.type'
import { getOnePlaybook, applyRunStateUpdate } from '../../../../services/playbook.service'
import { usePlaybookSocket } from '../../../../hooks/usePlaybookSocket'

import styles from './styles.module.scss'

export enum SidebarMode {
  Add = 'add',
  EventLog = 'eventlog',
  Reports = 'reports',
}

interface IProps {
  mode: SidebarMode | null
}

const Sidebar: FC<IProps> = ({ mode }) => {
  const theme = useTheme()

  const sidebarRef = useRef<HTMLDivElement>(null)
  const { playbook, setPlaybook, setPlaybooks, appendEvent, setRunState } = useContext(PlaybookContext)
  const events = playbook?.events || {}

  // Scroll events list as they arrive:
  useEffect(() => {
    if (playbook?.events && sidebarRef.current && mode === SidebarMode.EventLog) {
      sidebarRef.current.scrollTo({
        top: sidebarRef.current.scrollHeight,
        behavior: 'smooth',
      })
    } else if (sidebarRef.current) {
      sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [playbook?.events, mode])

  // Subscribe to socket.io events:
  const playbookId = playbook?.id
  const playbookIds = useMemo(() => (playbookId ? [playbookId] : []), [playbookId])

  const handleEvent = useCallback(
    async (ev: IEvent) => {
      console.log('event', ev)
      setRunState((old) => ev.run_state_updates.reduce(applyRunStateUpdate, old))

      // Update the list of events.
      // If we are still running, just change the events on the playbook.
      // If the run failed (or succeeded), update the playbook status too,
      // especially so that we redraw start/stop icon.
      if (ev.run.status === 'running') {
        appendEvent(playbookId!, ev)
      } else {
        const resp = await getOnePlaybook(ev.playbook.id.toString())
        if (resp.success && playbookId && resp.data) {
          setPlaybook((pb) => pb && { ...pb, ...resp.data })
          setPlaybooks((prev) => prev.map((pb) => (pb.id === ev.playbook.id ? { ...pb, ...resp.data } : pb)))
        }
      }
    },
    [playbookId, setPlaybook, setRunState, appendEvent],
  )

  usePlaybookSocket(playbookIds, handleEvent)

  return (
    <Box
      className={styles.sidebar}
      ref={sidebarRef}
      style={{
        width: mode ? 500 : 0,
        borderLeft: '1px solid ' + theme.palette.elevated.dark,
      }}
    >
      {mode === SidebarMode.Add && <DragAndDrop />}
      {mode === SidebarMode.EventLog && (
        <Box className={styles.events}>
          {Object.keys(events).map((value, ind) => (
            <EventsGroup
              key={value}
              events={events[value].events}
              created_at={events[value].eventsDate}
              runNumber={value}
              open={playbook?.status === 'running' && Object.keys(events).length - 1 === ind}
            />
          ))}
        </Box>
      )}
      {mode === SidebarMode.Reports && <PlaybookReportsPanel />}
    </Box>
  )
}

export default Sidebar
